import React, { createContext, forwardRef, Fragment, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { YMapContext, YMapPackage, YMapProvider } from '../store/YMapProvider';
import useIsomorphicEffect from '../hooks/useIsomorphicEffect';
import Marker from './icons/Marker';
import log from '../utils/logger';
import { env } from '../utils/util';

const pointsEqual = (p1, p2) => {
  if (p1.length !== p2.length) return false;
  return p1.every((point, i) => {
    return point.properties?.name === p2[i].properties?.name && point.geometry?.coordinates[0] === p2[i].geometry?.coordinates[0] && point.geometry?.coordinates[1] === p2[i].geometry?.coordinates[1];
  });
};

const POINT_MODE = {
  CLUSTER: 'cluster',
  POINTS: 'points',
}

class YMaps3 extends React.Component {
  constructor(props) {
    super(props);

    const longitude = props.longitude ?? 37.61764526;
    const latitude = props.latitude ?? 55.75581741;
    const points = props.points;
    const point = points?.find((point) => point.geometry.coordinates[0] === longitude && point.geometry.coordinates[1] === latitude);

    this.onSelectPoint = typeof props.onSelectPoint === 'function' ? props.onSelectPoint : () => {};
    this.onSelectAddress = typeof props.onSelectAddress === 'function' ? props.onSelectAddress : () => {};

    this.state = {
      ymap: null,
      location: {center: [props.longitude, props.latitude], zoom: props.zoom, easing: 'ease-in-out', duration: 1000},
      longitude,
      latitude,
      zoom: props.zoom ?? 10,
      points,
      point,
      address: props.address ?? null,
      marker: null,
      point_mode: props.point_mode ?? POINT_MODE.POINTS,
    };

  }

  componentDidMount() {
    if (this.state.address) {
      this.search(this.state.address);
    }
  }

  componentDidUpdate(prevProps) {
    let state = null;
    if (this.props.hasOwnProperty('latitude') && this.props.latitude !== this.state.latitude) {
      state = state ?? {};
      state.latitude = this.props.latitude;
      state.location = {...this.state.location};
      state.location.center[1] = state.latitude;
    }

    if (this.props.hasOwnProperty('longitude') && this.props.longitude !== this.state.longitude) {
      state = state ?? {};
      state.longitude = this.props.longitude;
      state.location = {...this.state.location};
      state.location.center[0] = state.longitude;
    }

    if (this.props.hasOwnProperty('zoom') && this.props.zoom !== this.state.zoom) {
      state = state ?? {};
      state.zoom = this.props.zoom;
      state.location = {...this.state.location}
      state.location.zoom = state.zoom;
    }

    if (this.props.hasOwnProperty('point_mode') && this.props.point_mode !== this.state.point_mode) {
      state = state ?? {};
      state.point_mode = this.props.point_mode;
    }

    if (this.props.hasOwnProperty('points') && !pointsEqual(this.props.points, this.state.points)) {
      state = state ?? {};
      state.points = this.props.points;
    }

    if (this.props.hasOwnProperty('address') && this.props.address !== this.state.address) {
      state = state ?? {};
      state.address = this.props.address;
      if (state.address) {
        this.search(state.address, () => {}, true);
      }
    }

    if (state) {
      if (typeof state.longitude === 'number' && typeof state.latitude === 'number') {
        state.point = this.state.points?.find((point) => point.geometry.coordinates[0] === state.longitude && point.geometry.coordinates[1] === state.latitude);
      }
      this.setState({ ...prevProps, ...state });
    }
  }

  setYmap(ymap) {
    this.setState((prevProps) => ({ ...prevProps, ymap }));
    if (this.state.address) {
      this.search(this.state.address, false, true, ymap);
    }
  };

  getBounds(coordinates) {
    let minLat = Infinity;
    let minLng = Infinity;
    let maxLat = -Infinity;
    let maxLng = -Infinity;

    for (const coords of coordinates) {
      const lat = coords[1];
      const lng = coords[0];

      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    }

    return [
      [minLng, minLat],
      [maxLng, maxLat]
    ];
  }

  marker(feature, selected) {
    return (
      <>
        <YMapMarker id={feature.id} coordinates={feature.geometry.coordinates}>
          <div
            className={`ymap-marker ${(selected ?? (feature.id === this.state.point?.id)) ? 'selected' : ''}`}
            title={feature.properties.name}
          >
            <Marker label={feature.id}/>
          </div>
        </YMapMarker>
      </>
    );
  }

  cluster(coordinates, features) {
    return (
      <>
        <YMapMarker coordinates={coordinates}>
          <div className="circle" onClick={() => this.onClusterClick(features)}>
            <div className="circle-content">
              <span className="circle-text">{features.length}</span>
            </div>
          </div>
        </YMapMarker>
      </>
    );
  }

  getHint(feature) {
    return feature?.properties?.name ?? this.state.point?.properties?.name;
  }

  onClusterClick(features) {
    const bounds = this.getBounds(features.map((feature) => feature.geometry.coordinates));
    this.setState((prevProps) => ({ ...prevProps, location: {...prevProps.location, bounds}}));
  }

  onMapClick(object, event) {
    if (this.state.points instanceof Array && this.state.points.length > 0 && object?.type === 'marker') {
      let point = this.state.points?.find((point) => point.geometry.coordinates[0] === object.entity.coordinates[0] && point.geometry.coordinates[1] === object.entity.coordinates[1]);
      this.onSelectPoint(point);
      this.setState((prevProps) => ({ ...prevProps, point, marker: null }));
    } else if (!(this.state.points instanceof Array) || this.state.points.length === 0) {
      this.search(event.coordinates, (data) => {
        this.onSelectAddress(data?.properties?.name, data?.geometry?.coordinates);
      });
    }
  }

  search(text, callback, center = false, ymap) {
    try {
      (this.state.ymap ?? ymap)?.search({text}).then((data) => {
        if (data.length > 0) {
          if (data[0].geometry?.type === 'Point') {
            let address = [];
            if (data[0].properties?.description) {
              address.push(data[0].properties.description);
            }
            if (data[0].properties?.name) {
              address.push(data[0].properties.name);
            }

            let marker = new PointFeature({ id: 'address', longitude: data[0].geometry.coordinates[0], latitude: data[0].geometry.coordinates[1], label: address.join(', ')});

            typeof callback === 'function' && callback(marker);

            if (center) {
              let location = {...this.state.location};
              location.center[0] = data[0].geometry.coordinates[0];
              location.center[1] = data[0].geometry.coordinates[1];
              this.setState((prevProps) => ({ ...prevProps, location, marker, point: null}));
            } else {
              this.setState((prevProps) => ({ ...prevProps, marker, point: null}));
            }
          }
        }
      }).catch((e) => {
        log.error(e);
      });
    } catch(e) {
      log.error(e);
    }
  }


  points(points = []) {
    return points.map((point, i) => ({
      type: 'Feature',
      id: i.toString(),
      geometry: {type: 'Point', coordinates: [point.longitude, point.latitude]},
    }));
  }

  render() {
    return (
      <YMapProvider>
        <YMapContext.Consumer>
          {({ ymaps3 }) => (
            <YMap key="map" className={this.props.className} ref={() => this.setYmap(ymaps3().ymaps)} coordorder="longlat" location={this.state.location} mode="vector">
              <YMapDefaultSchemeLayer />
              <YMapDefaultFeaturesLayer />

              {this.state.point_mode === POINT_MODE.CLUSTER ? (
                // <YMapClusterer marker={marker} cluster={cluster} method={gridSizedMethod} features={points} />
                <YMapCustomClusterer
                  marker={(feature) => this.marker(feature)}
                  cluster={(coordinates, features) => this.cluster(coordinates, features)}
                  method={() => this.gridSizedMethod()}
                  features={this.state.points}
                />
              ) : (
                <>
                  {this.state.points?.map((feature, i) => (
                    <Fragment key={i}>
                      {this.marker(feature)}
                    </Fragment>
                  ))}
                </>
              )}

              {this.state.marker && (
                <>
                  <YMapMarker id={this.state.marker.id} coordinates={this.state.marker.geometry.coordinates}>
                    <div className={`ymap-marker selected`} title={this.state.marker.properties.name}>
                      <Marker/>
                    </div>
                  </YMapMarker>
                </>
              )}

              <YMapListener onClick={(object, event) => this.onMapClick(object, event)} />

              <YMapControls position="bottom">
                <YMapZoomControl />
              </YMapControls>
            </YMap>
          )}
        </YMapContext.Consumer>
      </YMapProvider>
    );
  }
};

YMaps3.propTypes = {
  className: PropTypes.string,
  points: PropTypes.array,
  longitude: PropTypes.number,
  latitude: PropTypes.number,
  zoom: PropTypes.number,
  address: PropTypes.string,
  point_mode: PropTypes.oneOf(Object.values(POINT_MODE)),
  onSelectPoint: PropTypes.func,
  onSelectAddress: PropTypes.func,
};


const YMapHintContext = createContext();

const YMapHintWrapper = ({children, context}) => {
  const hint = useContext(context);
  return (
    <YMapHintContext.Provider value={hint}>{children ?? hint?.hint}</YMapHintContext.Provider>
  );
};


const YMapCustomHint = ({ children, hint }) => {
  const ymapInfo = useContext(YMapContext);
  const {reactify, ymaps} = ymapInfo.ymaps3();
  const [ymapPackage, setYMapPackage] = ymapInfo.usePackage(YMapPackage.Hint);
  const [hintContext, setHintContext] = useState();

  useEffect(() => {
    if (ymapPackage) {
      setHintContext(reactify.module(ymapPackage)['YMapHintContext']);
    }
  }, [ymapPackage]);

  useEffect(() => {
    if (!ymapPackage) {
      ymaps.import(YMapPackage.Hint).then((data) => {
        setYMapPackage(data);
      });
    }
  }, []);

  if (!YMapHint || !hintContext) {
    return <></>;
  } else {
    return (
      <YMapHint hint={hint}>
        <YMapHintWrapper context={hintContext}>{children}</YMapHintWrapper>
      </YMapHint>
    );
  }
};



const YMapCustomClusterer = forwardRef(({ gridSize, method, ...props }, ref) => {
  const ymapInfo = useContext(YMapContext);
  const {reactify, ymaps} = ymapInfo.ymaps3();
  const [ymapPackage, setYMapPackage] = ymapInfo.usePackage(YMapPackage.Clusterer);

  const [clusterByGrid] = useMemo(() => {
    if (ymapPackage) {
      return [method || reactify.module(ymapPackage)['clusterByGrid']];
    } else {
      return [];
    }
  }, [ymapPackage]);

  useEffect(() => {
    if (!ymapPackage) {
      ymaps.import(YMapPackage.Clusterer).then((data) => {
        setYMapPackage(data);
      });
    }
  }, []);

  const gridSizedMethod = useMemo(() => {
    if (clusterByGrid && gridSize) {
      return clusterByGrid({ gridSize });
    }
  }, [clusterByGrid, gridSize]);

  if (!YMapClusterer || !gridSizedMethod) {
    return <></>;
  } else {
    return (
      <YMapClusterer
        ref={ref}
        method={gridSizedMethod}
        {...props}
      />
    );
  }
});


const loadPackage = (componentName, ymapPackageSrc) => {
  let YMapComponent = null;

  const Component = ({ ...props }, ref) => {
    const ymapInfo = useContext(YMapContext);
    const {reactify, ymaps} = ymapInfo.ymaps3();
    const [isLoaded, setIsLoaded] = useState(false);
    const [ymapPackage, setYMapPackage] = ymapInfo.usePackage(ymapPackageSrc);

    useIsomorphicEffect(() => {
      if (!ymapPackageSrc) {
        setIsLoaded(true);
        YMapComponent = reactify.module(ymaps)[componentName];
      } else {
        if (ymapPackage) {
          setIsLoaded(true);
          YMapComponent = reactify.module(ymapPackage)[componentName];
        } else {
          ymaps.import(ymapPackageSrc).then((data) => {
            setYMapPackage(data);
            if (reactify.module(data)) {
              setIsLoaded(true);
              YMapComponent = reactify.module(data)[componentName];
            }
          }).catch((e) => {
            log.error(e);
          });
        }
      }

    }, []);

    if (!YMapComponent || !isLoaded) {
      return <></>;
    } else {
      return <YMapComponent ref={ref} {...props} />;
    }
  };

  return forwardRef(Component);
}

export const YMapGeolocationControl = loadPackage('YMapGeolocationControl', YMapPackage.Controls);
export const YMapZoomControl = loadPackage('YMapZoomControl', YMapPackage.Controls);
export const YMapClusterer = loadPackage('YMapClusterer', YMapPackage.Clusterer);
export const YMapDefaultMarker = loadPackage('YMapDefaultMarker', YMapPackage.Markers);
export const YMapHint = loadPackage('YMapHint', YMapPackage.Hint);
export const YMap = loadPackage('YMap');
export const YMapTileDataSource = loadPackage('YMapTileDataSource');
export const ThemeContext = loadPackage('ThemeContext');
export const YMapControl = loadPackage('YMapControl');
export const YMapLayer = loadPackage('YMapLayer');
export const YMapMarker = loadPackage('YMapMarker');
export const YMapDefaultSchemeLayer = loadPackage('YMapDefaultSchemeLayer');
export const YMapDefaultFeaturesLayer = loadPackage('YMapDefaultFeaturesLayer');
export const YMapDefaultSatelliteLayer = loadPackage('YMapDefaultSatelliteLayer');
export const YMapListener = loadPackage('YMapListener');
export const YMapControls = loadPackage('YMapControls');
export const YMapControlButton = loadPackage('YMapControlButton');
export const YMapContainer = loadPackage('YMapContainer');
export const YMapCollection = loadPackage('YMapCollection');
export const YMapFeature = loadPackage('YMapFeature');
export const YMapFeatureDataSource = loadPackage('YMapFeatureDataSource');

export class PointFeature {
  constructor({longitude, latitude, id, label, meta = {}}) {
    this.type = 'Feature';
    this.id = `${id}`;
    this.geometry = { coordinates: [longitude, latitude], type: 'Point' };
    this.properties = { name: label };
    this.meta = meta;
  }
};
export const ymapAvailable = !!env('REACT_APP_MAP_API_KEY');

export default YMaps3;
